import React from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Avatar,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import {
  useListContext,
  linkToRecord,
  RecordContextProvider,
} from "react-admin";

/**
 * SimpleList
 * - Reimplements RA's SimpleList rendering using RA hooks
 * - Adds rowClick(record, id, basePath) for custom navigation (e.g. showInvoice)
 * - If you still want default RA navigation, pass linkType: 'edit' | 'show' | true | false
 *   - If linkType is truthy, list items render wrapped in <Link>
 *   - If linkType is falsy, we attach onClick to the ListItem instead
 */
export default function SimpleList({
  className,
  primaryText,
  secondaryText,
  tertiaryText,
  leftAvatar,
  leftIcon,
  rightAvatar,
  rightIcon,
  rowStyle,
  linkType = "edit", // same default as RA SimpleList
  rowClick, // (record, id, basePath) => void
  ...rest
}) {
  const { basePath, data, ids, loaded, total } = useListContext(rest);

  if (loaded === false) return null; // or your own loading skel

  const renderAvatar = (record, id, renderFn) => {
    const val = renderFn(record, id);
    if (
      typeof val === "string" &&
      (val.startsWith("http") || val.startsWith("data:"))
    ) {
      return <Avatar src={val} />;
    }
    return <Avatar>{val}</Avatar>;
  };

  const wrapMaybeLink = (record, id, children) => {
    // Maintain RA SimpleList's linkType behavior
    const resolved =
      typeof linkType === "function" ? linkType(record, id) : linkType;

    if (resolved === "edit" || resolved === true) {
      return <Link to={linkToRecord(basePath, id)}>{children}</Link>;
    }
    if (resolved === "show") {
      return <Link to={`${linkToRecord(basePath, id)}/show`}>{children}</Link>;
    }
    // No link — make it clickable if rowClick provided
    return (
      <span
        onClick={rowClick ? () => rowClick(record, id, basePath) : undefined}
        style={{ display: "block", cursor: rowClick ? "pointer" : "default" }}
      >
        {children}
      </span>
    );
  };

  return (
    total > 0 && (
      <List className={className} {...rest}>
        {ids.map((id, idx) => {
          const record = data[id];
          if (!record) return null;

          const primary =
            typeof primaryText === "function"
              ? primaryText(record, id)
              : primaryText || null;
          const secondary =
            typeof secondaryText === "function"
              ? secondaryText(record, id)
              : secondaryText || null;
          const tertiary =
            typeof tertiaryText === "function"
              ? tertiaryText(record, id)
              : tertiaryText || null;

          const leftIconNode =
            typeof leftIcon === "function" ? leftIcon(record, id) : null;
          const leftAvatarNode =
            typeof leftAvatar === "function"
              ? renderAvatar(record, id, leftAvatar)
              : null;

          const rightIconNode =
            typeof rightIcon === "function" ? rightIcon(record, id) : null;
          const rightAvatarNode =
            typeof rightAvatar === "function"
              ? renderAvatar(record, id, rightAvatar)
              : null;

          const item = (
            <ListItem
              key={id}
              button // keeps hover/ink behavior; link vs click handled by wrapper
              style={rowStyle ? rowStyle(record, idx) : undefined}
            >
              {leftIconNode && <ListItemIcon>{leftIconNode}</ListItemIcon>}
              {leftAvatarNode && <ListItemIcon>{leftAvatarNode}</ListItemIcon>}

              <ListItemText
                primary={
                  tertiary ? (
                    <div style={{ display: "flex", alignItems: "baseline" }}>
                      <span>{primary}</span>
                      <span
                        style={{
                          marginLeft: "auto",
                          opacity: 0.54,
                          fontSize: "0.875rem",
                        }}
                      >
                        {tertiary}
                      </span>
                    </div>
                  ) : (
                    primary
                  )
                }
                secondary={secondary}
              />

              {(rightIconNode || rightAvatarNode) && (
                <ListItemSecondaryAction>
                  {rightAvatarNode}
                  {rightIconNode && (
                    <ListItemIcon>{rightIconNode}</ListItemIcon>
                  )}
                </ListItemSecondaryAction>
              )}
            </ListItem>
          );

          return (
            <RecordContextProvider key={id} value={record}>
              {wrapMaybeLink(record, id, item)}
            </RecordContextProvider>
          );
        })}
      </List>
    )
  );
}
